import useBadgeSession, { isBadgeSessionCurrent } from './useBadgeSession';
import { useEffect, useRef, useState } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { useUserStore } from '../../stores/userStore';
import api from '../../api/api';
import { badgeKey } from './useBadges';
import { earnedBadges, visibleBadge } from './model';
import { runBadgeCollectionTask } from './collectionTasks';
export default function useCelebrations(data, enabled, target, root, restoreVisit = false) {
  const session = useBadgeSession();
  const userId = useUserStore((s) => s.user?.id),
    client = useQueryClient(),
    completed = useRef(new Set()),
    moved = useRef(false),
    [visible, setVisible] = useState([]);
  useEffect(() => {
    completed.current.clear();
  }, [userId, session]);
  useEffect(() => {
    moved.current = restoreVisit;
  }, [userId, target, session, restoreVisit]);
  useEffect(() => {
    if (!enabled || !data || !userId) return;
    const unseen = (data.unseenBadgeKeys ?? []).filter(
      (k) => earnedBadges(data).some((b) => b.key === k) && !completed.current.has(k),
    );
    const media = window.matchMedia('(prefers-reduced-motion: reduce)'),
      pending = new Set();
    let timer,
      animation,
      ack,
      disposed = false;
    const credential = localStorage.getItem('POSTPOP_AUTH_TOKEN');
    const sameSession = () =>
      isBadgeSessionCurrent(session) &&
      useUserStore.getState().user?.id === userId &&
      localStorage.getItem('POSTPOP_AUTH_TOKEN') === credential;
    const current = () =>
      !disposed &&
      !document.hidden &&
      sameSession() &&
      !Array.from(document.querySelectorAll('[role="dialog"]')).some(
        (node) => node.getClientRects().length > 0,
      );
    const bounds = () => {
      const header =
        document.querySelector('.pp-site-header-inner')?.closest('header') ??
        document.querySelector('.header-mobile');
      const player = document.querySelector('.pp-preview-player');
      const achievement = document.querySelector('.pp-achievement');
      const noticeBottom = achievement?.getClientRects().length
        ? achievement.getBoundingClientRect().bottom
        : 0;
      const playerTop = player?.getClientRects().length
        ? player.getBoundingClientRect().top
        : window.innerHeight;
      return [
        Math.max(56, header?.getBoundingClientRect().bottom ?? 0, noticeBottom),
        Math.min(window.innerHeight - 110, playerTop),
      ];
    };
    const flush = () => {
      // These cards already completed their visible animation. Route/data cleanup
      // must not drop that confirmation; authentication is the remaining boundary.
      if (!sameSession() || !pending.size) return;
      const keys = [...pending];
      pending.clear();
      void runBadgeCollectionTask(client, userId, async () => {
        if (!sameSession()) return;
        await api.putAuth(
          '/me/badges/seen',
          { badgeKeys: keys, expectedUserId: userId },
          { suppressGlobalError: true },
        );
        if (!sameSession()) return;
        await client.cancelQueries({ queryKey: badgeKey(userId), exact: true });
        if (!sameSession()) return;
        client.setQueryData(
          badgeKey(userId),
          (old) =>
            old && {
              ...old,
              unseenBadgeKeys: old.unseenBadgeKeys?.filter((k) => !keys.includes(k)),
            },
        );
      }).catch(() => {});
    };
    const settle = () => {
      clearTimeout(timer);
      clearTimeout(animation);
      setVisible([]);
      if (!current()) return;
      if (pending.size) flush();
      timer = setTimeout(() => {
        if (!current()) return;
        // POP restoration owns position until data and geometry have settled.
        if (root.current?.dataset.profileRestoring === 'true') {
          settle();
          return;
        }
        const cards = Array.from(root.current?.querySelectorAll('[data-badge-key]') ?? []),
          focus = cards.find((c) => c.dataset.badgeKey === (target ?? unseen[0]));
        if (!moved.current && focus) {
          moved.current = true;
          const y = focus.getBoundingClientRect().top + window.scrollY - bounds()[0] - 24;
          window.scrollTo({ top: Math.max(0, y), behavior: media.matches ? 'auto' : 'smooth' });
          settle();
          return;
        }
        const safe = cards
          .filter(
            (c) =>
              unseen.includes(c.dataset.badgeKey) &&
              !completed.current.has(c.dataset.badgeKey) &&
              visibleBadge(c.getBoundingClientRect(), ...bounds()),
          )
          .map((c) => c.dataset.badgeKey);
        if (!safe.length) return;
        setVisible(safe);
        animation = setTimeout(
          () => {
            if (!current()) return;
            safe.forEach((k) => {
              const c = cards.find((v) => v.dataset.badgeKey === k);
              if (c && visibleBadge(c.getBoundingClientRect(), ...bounds())) {
                completed.current.add(k);
                pending.add(k);
              }
            });
            setVisible([]);
            clearTimeout(ack);
            ack = setTimeout(flush, 200);
          },
          media.matches ? 0 : 700,
        );
      }, 140);
    };
    const drag = () => {
      moved.current = true;
      settle();
    };
    window.addEventListener('scroll', settle, { passive: true });
    window.addEventListener('resize', settle);
    window.addEventListener('wheel', drag, { passive: true });
    window.addEventListener('touchstart', drag, { passive: true });
    window.addEventListener('keydown', drag);
    document.addEventListener('visibilitychange', settle);
    media.addEventListener?.('change', settle);
    settle();
    return () => {
      flush();
      disposed = true;
      clearTimeout(timer);
      clearTimeout(animation);
      clearTimeout(ack);
      window.removeEventListener('scroll', settle);
      window.removeEventListener('resize', settle);
      window.removeEventListener('wheel', drag);
      window.removeEventListener('touchstart', drag);
      window.removeEventListener('keydown', drag);
      document.removeEventListener('visibilitychange', settle);
      media.removeEventListener?.('change', settle);
    };
  }, [data, enabled, target, userId, client, root, session]);
  return visible;
}
